import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Shield, Clock, AlertTriangle } from 'lucide-react';

interface RateLimitEntry {
  id: string;
  identifier: string;
  endpoint: string;
  attempt_count: number;
  first_attempt_at: string;
  last_attempt_at: string;
  blocked_until: string | null;
}

export const RateLimitMonitor = () => {
  const [blockedEntries, setBlockedEntries] = useState<RateLimitEntry[]>([]);
  const [recentAttempts, setRecentAttempts] = useState<RateLimitEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchData = async () => {
    try {
      // Blocked entries
      const { data: blocked } = await supabase
        .from('auth_rate_limits')
        .select('*')
        .not('blocked_until', 'is', null)
        .gt('blocked_until', new Date().toISOString())
        .order('blocked_until', { ascending: false });

      setBlockedEntries(blocked || []);

      // Recent attempts (last hour)
      const oneHourAgo = new Date(Date.now() - 3600000).toISOString();
      const { data: recent } = await supabase
        .from('auth_rate_limits')
        .select('*')
        .gt('last_attempt_at', oneHourAgo)
        .order('attempt_count', { ascending: false })
        .limit(20);

      setRecentAttempts(recent || []);
      setIsLoading(false);
    } catch (error) {
      console.error('Error fetching rate limit data:', error);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000); // Refresh every 30s
    
    return () => clearInterval(interval);
  }, []);

  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getEndpointBadgeVariant = (endpoint: string) => {
    switch (endpoint) {
      case 'login': return 'default';
      case 'qr-auth': return 'secondary';
      default: return 'outline';
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-destructive" />
            Currently Blocked ({blockedEntries.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {blockedEntries.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Shield className="h-12 w-12 mx-auto mb-2 opacity-50" />
              <p>No blocked users at the moment</p>
            </div>
          ) : (
            <div className="space-y-3">
              {blockedEntries.map((item) => (
                <div key={item.id} className="flex flex-col gap-2 p-3 rounded-lg bg-muted/50 border border-border">
                  <div className="flex items-center justify-between">
                    <code className="text-sm font-mono bg-background px-2 py-1 rounded">
                      {item.identifier}
                    </code>
                    <Badge variant={getEndpointBadgeVariant(item.endpoint)}>
                      {item.endpoint}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Clock className="h-3 w-3" />
                    <span>Blocked until: {formatTime(item.blocked_until!)}</span>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Attempts: {item.attempt_count}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-primary" />
            Recent Activity (Last Hour)
          </CardTitle>
        </CardHeader>
        <CardContent>
          {recentAttempts.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <p>No recent activity</p>
            </div>
          ) : (
            <div className="space-y-2">
              {recentAttempts.map((item) => (
                <div key={item.id} className="flex items-center justify-between p-2 rounded-lg hover:bg-muted/50 transition-colors">
                  <div className="flex items-center gap-3">
                    <code className="text-xs font-mono bg-background px-2 py-1 rounded border border-border">
                      {item.identifier}
                    </code>
                    <Badge variant={getEndpointBadgeVariant(item.endpoint)} className="text-xs">
                      {item.endpoint}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="text-sm font-medium">
                      {item.attempt_count} {item.attempt_count === 1 ? 'attempt' : 'attempts'}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {formatTime(item.last_attempt_at)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
